// The teams a coach code or a team code covers, as the "Is this your team?"
// pickers show them (the V2 onboarding TeamCodeStep and CoachCodeVerifier).
//
// The 17U, 16U and 15U squads of one Cerebro program share the program id
// and differ by league, so a row is told apart by its key (id and league,
// `teamKey` from the server), never by the id or the team name alone. The
// link request sends the picked row's league with its id for the same
// reason.
//
// A program code (notes of 10/19, D14: one code for every age group of a
// program) previews with scope 'program', the program's name and one row
// per squad of the athlete's gender, each with its own wouldAddTeam,
// problem and coach; the athlete picks their squad.
import { ageGroupOf, displayTeamName, stripSeasonSuffix } from "../../../utils/teamNames";

export const codeTeamKey = (t) => t?.teamKey || `${t?.teamId || t?.teamName || ""}|${t?.league || ""}`;

// A raw Cerebro league name reads as people say it: "Nike EYBL (Boys)|16U"
// becomes "Nike EYBL (Boys) 16U".
export const cleanLeagueLabel = (text) => {
  const [name = "", division = ""] = String(text || "").split("|");
  const n = name.replace(/\s+/g, " ").trim();
  const d = division.replace(/\s+/g, " ").trim();
  return d && !n.toLowerCase().includes(d.toLowerCase()) ? `${n} ${d}`.trim() : n;
};

// A row's title: the team's display name, with the squad's age group
// ("Oakland Soldiers 16U"; the server sends displayName, else the same
// rule from its key).
export const codeTeamTitle = (t) => displayTeamName(t) || String(t?.teamName || "").trim() || "Team";

const sameText = (x, y) => String(x || "").trim().toLowerCase() === String(y || "").trim().toLowerCase();

// The line under a team: its own league (with the age group, without a
// season CACHE added), then any `extra` parts, its gender and its level. A
// level that only repeats the age group the title or the league already
// say is left out.
export const codeTeamMeta = (t, extra = []) => {
  const league = cleanLeagueLabel(stripSeasonSuffix(t?.leagueLabel || ""));
  const age = ageGroupOf(t);
  const repeats = !!age && sameText(t?.level, age) && (codeTeamTitle(t).includes(age) || league.includes(age));
  return [league, ...extra, t?.gender, repeats ? "" : t?.level].filter(Boolean).join(" · ");
};

// A program code (every age group of one program).
export const isProgramCode = (preview) => preview?.scope === "program";

// "This code covers every age group of Oakland Soldiers. Pick yours." for a
// program code, "" for any other.
export const programPickNote = (preview) => {
  if (!isProgramCode(preview)) return "";
  const name = String(preview.programName || "").trim();
  return name ? `This code covers every age group of ${name}. Pick yours.` : "This code covers every age group of the program. Pick yours.";
};

// What the picked row says about linking: its own problem and whether it is
// added beside the athlete's main team (a program code answers per squad),
// else the preview's.
export const codeProblem = (preview, chosen) => (chosen && "problem" in chosen ? chosen.problem || null : preview?.problem || null);
export const codeWouldAdd = (preview, chosen) => (chosen && "wouldAddTeam" in chosen ? !!chosen.wouldAddTeam : !!preview?.wouldAddTeam);

// The row a picker starts on. A code for one team starts on it. A code for
// several (the 17U, 16U and 15U squads of one program) starts on the
// athlete's own squad only when the server knows it for certain (`yours`: a
// roster spot the athlete holds there) and it can still be linked; else no
// row is picked and Confirm waits for the athlete's pick (9/29 review: a 16U
// player confirmed the 17U squad the picker had picked for them).
export const defaultCodeTeam = (teams = []) => {
  if (teams.length <= 1) return teams[0] || null;
  const mine = teams.filter((t) => t.yours && !t.alreadyLinked);
  return mine.length === 1 ? mine[0] : null;
};

// The picked row: the one whose key is `pick`, else a code's only team.
// null while a code for several teams has no pick.
export const pickedCodeTeam = (teams = [], pick = "") =>
  teams.find((t) => codeTeamKey(t) === pick) || (teams.length === 1 ? teams[0] : null);

// Every team the code covers is one the athlete is verified on already.
export const allCodeTeamsLinked = (teams = []) => teams.length > 0 && teams.every((t) => t.alreadyLinked);

// The team arguments of linkCoachCode for the picked row: its id, and its
// league when it has one.
export const codeTeamLinkArgs = (t) => (t?.league ? [t.teamId || undefined, t.league] : [t?.teamId || undefined]);

// The sentence a team link answers with when the team says another gender
// than the profile keeps (the server's fillNote: "This team is listed as a
// boys team. Your profile keeps the gender you picked."), else "".
export const fillNoteOf = (data) => (data && typeof data.fillNote === "string" ? data.fillNote.trim() : "");

// The server's sentence for a refused code ("Invalid or unverified code.",
// a team of another sport), never a 5xx body's text, which can carry a
// provider's raw error.
export const codeRefusal = (err, fallback) => {
  const status = err?.response?.status ?? err?.status;
  const data = err?.response?.data ?? err?.data;
  const text = data && typeof data === "object" ? data.error || data.message : "";
  return (!status || status < 500) && typeof text === "string" && text.trim() ? text.trim() : fallback;
};
